/**
 * GutachtenCrossLinkCard — Issue #273
 * ------------------------------------
 * Reverse cross-link UI shown inside the Mietwagen-Akten-Detail view.
 *
 * When a Gutachten-Akte has been linked to this Mietwagen-Akte
 * (i.e. akten.mietwagen_akte_id = <this akte's id>), this card
 * surfaces a clickable chip:
 *
 *   "Gutachten: AK2602162 · Sevinc, Kaan →"
 *
 * Clicking navigates to the Gutachten-Akte detail page.
 *
 * The lookup is a reverse query:
 *   SELECT id, akten_nummer, akten_nr, kunde_name
 *   FROM akten
 *   WHERE mietwagen_akte_id = <mietwagenAkteId>
 *     AND business_unit = 'gutachten'
 *   LIMIT 1
 *
 * Returns null (renders nothing) when no Gutachten-Akte is linked, so
 * the card never leaves an empty white box in the Mietwagen view.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { FileText, ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';

interface LinkedGutachtenAkte {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  kunde_name: string | null;
}

export interface GutachtenCrossLinkCardProps {
  /** UUID of the current Mietwagen-Akte — used for the reverse lookup. */
  mietwagenAkteId: string;
}

export default function GutachtenCrossLinkCard({ mietwagenAkteId }: GutachtenCrossLinkCardProps) {
  const [linked, setLinked] = useState<LinkedGutachtenAkte | null>(null);
  const [loading, setLoading] = useState(true);
  const [, navigate] = useLocation();
  const lastFetchedId = useRef<string | null>(null);

  useEffect(() => {
    if (!mietwagenAkteId || lastFetchedId.current === mietwagenAkteId) return;
    lastFetchedId.current = mietwagenAkteId;

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from('akten')
          .select('id, akten_nummer, akten_nr, kunde_name')
          .eq('mietwagen_akte_id', mietwagenAkteId)
          .eq('business_unit', 'gutachten')
          .maybeSingle();
        if (cancelled) return;
        setLinked((data as LinkedGutachtenAkte | null) ?? null);
      } catch {
        if (!cancelled) setLinked(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mietwagenAkteId]);

  if (loading && !linked) {
    return (
      <div
        data-testid="gutachten-chip-loading"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-[12px] text-gray-500"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Gutachten-Verknüpfung wird geladen...
      </div>
    );
  }

  if (!linked) return null;

  const identifier = aktenIdentifier(linked);
  if (!identifier) return null;

  const href = buildAkteHref('gutachten', identifier);

  return (
    <a
      data-testid="gutachten-chip-link"
      href={href}
      onClick={(e) => {
        e.preventDefault();
        navigate(href);
      }}
      className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors group max-w-full"
    >
      <span className="w-6 h-6 rounded-md bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
        <FileText className="w-3.5 h-3.5" />
      </span>
      <span className="flex items-baseline gap-1.5 min-w-0">
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
          Gutachten:
        </span>
        <span className="text-[12px] font-mono font-semibold truncate">
          {identifier}
        </span>
        {linked.kunde_name && (
          <span className="text-[11px] opacity-70 truncate hidden sm:inline">
            · {linked.kunde_name}
          </span>
        )}
      </span>
      <ExternalLink className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 flex-shrink-0" />
    </a>
  );
}
